import { Button, Field, FieldLabel, Input } from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { api, unwrap } from "../lib/api";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [enroll, setEnroll] = useState<{
    secret: string;
    otpauthUrl: string;
  } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const session = useQuery({
    queryKey: ["admin-login-session"],
    queryFn: async () => {
      const response = await api.auth.me.get();
      if (response.error || !response.data || "error" in response.data) {
        return null;
      }
      return response.data;
    },
    retry: false,
  });

  const needsEnroll =
    enroll != null ||
    (session.data?.user?.isSuperadmin === true &&
      session.data.twoFactorEnabled === false);

  async function enter() {
    queryClient.clear();
    await router.invalidate();
    await navigate({ to: "/" });
  }

  const login = useMutation({
    mutationFn: async () => {
      if (challenge) {
        return unwrap(
          await api.auth.login["2fa"].post({ challenge, code: code.trim() }),
        );
      }
      return unwrap(await api.auth.login.post({ email, password }));
    },
    onSuccess: async (data) => {
      if ("twoFactor" in data && data.twoFactor) {
        setChallenge(data.challenge);
        return;
      }
      if (!("user" in data) || !data.user?.isSuperadmin) {
        throw new Error("Acesso restrito à equipe interna");
      }
      if ("mustEnrollTwoFactor" in data && data.mustEnrollTwoFactor) {
        const setup = unwrap(await api.auth["2fa"].setup.post());
        setEnroll(setup);
        return;
      }
      await enter();
    },
  });

  const confirm = useMutation({
    mutationFn: async () =>
      unwrap(await api.auth["2fa"].confirm.post({ code: code.trim() })),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
    },
  });

  const startEnroll = useMutation({
    mutationFn: async () => unwrap(await api.auth["2fa"].setup.post()),
    onSuccess: (data) => setEnroll(data),
  });

  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface p-4">
      <div className="w-full max-w-xs">
        <div className="mb-8 flex flex-col items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-surface bg-ink text-ink-inverse shadow-raised">
            <ShieldCheck className="size-5" />
          </div>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              Bridge Admin
            </h1>
            <p className="text-2xs text-ink-muted">
              {needsEnroll
                ? "A conta de admin exige um código no celular"
                : "Painel interno — acesso restrito"}
            </p>
          </div>
        </div>

        {backupCodes ? (
          <div className="flex flex-col gap-4">
            <p className="text-2xs text-ink-muted">
              Guarde estes códigos. Cada um entra uma vez se você perder o celular.
            </p>
            <ul className="rounded-control border border-line bg-surface-raised px-3 py-2 font-mono text-2xs">
              {backupCodes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <Button type="button" size="lg" onClick={() => void enter()}>
              Entrar no admin
            </Button>
          </div>
        ) : needsEnroll ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (!enroll) {
                startEnroll.mutate();
                return;
              }
              confirm.mutate();
            }}
          >
            {enroll ? (
              <>
                <p className="text-2xs text-ink-muted">
                  No app autenticador, use o endereço abaixo ou a chave manual.
                </p>
                <p className="break-all rounded-control border border-line bg-surface-raised px-3 py-2 font-mono text-2xs">
                  {enroll.secret}
                </p>
                <Field>
                  <FieldLabel>Código de 6 dígitos</FieldLabel>
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                </Field>
              </>
            ) : (
              <p className="text-2xs text-ink-muted">
                Gere a chave e confirme com o código do celular para liberar o painel.
              </p>
            )}
            {(confirm.isError || startEnroll.isError) && (
              <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
                Não foi possível ativar. Gere a chave de novo e use um código novo.
              </p>
            )}
            <Button
              type="submit"
              size="lg"
              loading={confirm.isPending || startEnroll.isPending}
            >
              {enroll ? "Confirmar" : "Gerar chave"}
            </Button>
          </form>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              login.mutate();
            }}
          >
            {!challenge && (
              <>
                <Field>
                  <FieldLabel>E-mail</FieldLabel>
                  <Input
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel>Senha</FieldLabel>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>
              </>
            )}
            {challenge && (
              <Field>
                <FieldLabel>Código do autenticador</FieldLabel>
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
            )}
            {login.isError && (
              <p className="rounded-control border border-danger/25 bg-danger-subtle px-3 py-2 text-2xs text-danger">
                Credenciais inválidas ou sem permissão de acesso.
              </p>
            )}
            <Button type="submit" size="lg" loading={login.isPending}>
              {challenge ? "Confirmar" : "Entrar"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
